import React, { useState } from "react";
import axios from "axios";

const EventPublicationTool = ({ backEndUrl, platformUrl }) => {
    const [eventContent, setEventContent] = useState("");
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [retrieving, setRetrieving] = useState(false);
    const [handling, setHandling] = useState(false);

    const generateEvents = async () => {
        setResponse(null);
        setError(null);

        try {
            const result = await axios.get(`${backEndUrl}/generateEvents`, {
                headers: {
                    "Content-Type": "application/json",
                },
                params: {
                    platformUrl, // Use platformUrl prop
                },
            });

            setEventContent(JSON.stringify(result.data.events || [], null, 2));
            setResponse("Events generated successfully!");
        } catch (err) {
            setError(err.message || "An error occurred while generating the events.");
        }
    };

    const publishEvents = async () => {
        setLoading(true);
        setResponse(null);
        setError(null);

        try {
            if (!eventContent) {
                throw new Error("Event content is required.");
            }

            const events = JSON.parse(eventContent);

            if (!Array.isArray(events) || events.length === 0) {
                throw new Error("Event content must be a valid JSON array.");
            }

            const result = await axios.post(
                `${backEndUrl}/sendEvents`,
                { events },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            setResponse(result.data.message || "Events published successfully!");
            setEventContent(""); // Clear the textarea after successful submission
        } catch (err) {
            setError(err.message || "An error occurred while publishing the events.");
        } finally {
            setLoading(false);
        }
    };

    const retrieveEvent = async () => {
        setRetrieving(true);
        setResponse(null);
        setError(null);

        try {
            const result = await axios.get(`${backEndUrl}/retrieveEvents`, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            setEventContent(JSON.stringify(result.data.events || [], null, 2));
        } catch (err) {
            setError(err.message || "An error occurred while retrieving the events.");
        } finally {
            setRetrieving(false);
        }
    };

    const handleEvent = async () => {
        setHandling(true);
        setResponse(null);
        setError(null);

        try {
            if (!eventContent) {
                throw new Error("Event content is required.");
            }

            const events = JSON.parse(eventContent);

            if (!Array.isArray(events) || events.length === 0) {
                throw new Error("Event content must be a valid JSON array.");
            }

            const result = await axios.post(
                `${backEndUrl}/handleEvents`,
                { events },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            setResponse(result.data.message || "Events handled successfully!");
            setEventContent(""); // Clear the textarea after successful handling
        } catch (err) {
            setError(err.message || "An error occurred while handling the events.");
        } finally {
            setHandling(false);
        }
    };

    return (
        <div className="event-publication-tool-container">
            <h1>Event Management Tool</h1>
            <textarea
                className="event-input"
                rows="20"
                placeholder="Enter your events as a JSON array or retrieve them..."
                value={eventContent}
                onChange={(e) => setEventContent(e.target.value)}
                required
            />
            <div className="button-group">
                <button
                    className="generate-events-button"
                    onClick={generateEvents}
                >
                    Generate Events
                </button>
                <button
                    className="publish-event-button"
                    onClick={publishEvents}
                    disabled={loading}
                >
                    {loading ? "Publishing..." : "Publish Events"}
                </button>
                <button
                    className="retrieve-event-button"
                    onClick={retrieveEvent}
                    disabled={retrieving}
                >
                    {retrieving ? "Retrieving..." : "Retrieve Events"}
                </button>
                <button
                    className="handle-event-button"
                    onClick={handleEvent}
                    disabled={handling}
                >
                    {handling ? "Handling..." : "Handle Events"}
                </button>
            </div>
            {response && <p className="success-message">{response}</p>}
            {error && <p className="error-message">{error}</p>}
        </div>
    );
};

export default EventPublicationTool;
